import { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping, faTruck, faBuildingWheat } from '@fortawesome/free-solid-svg-icons';

import ApiDirector from '../components/ApiDirector';
import { fetchMarket } from '../helpers/models';
import MarketEventDetail from '../components/market/MarketEventDetail';
import { useCache, resolveCache } from '../helpers/useCache';
import withAuthentication from '../helpers/withAuthentication';
import { toSnakeCase } from '../helpers/strings';

import {
  Page, Inset, Loading, DarkGreen, MediumGray, LightGray, Name, MediumGreen,
} from '../components/Styles';
import BreadCrumb from '../components/BreadCrumb';
import LinkButton from '../components/LinkButton';
import Card from '../components/Card';

import pear from './img/pear_icon.svg';
import cheese from './img/cheese_icon.svg';
import eggs from './img/eggs_icon.svg';
import cupcake from './img/cupcake_icon.svg';
import gift from './img/gift_icon.svg';
import jam from './img/jam_icon.svg';
import preOrder from './img/pre-order_icon.svg';
import priceTag from './img/price_tag_icon.svg';
import ZipModal from '../components/ZipModal';

const marketAvatarStyle = {
  avatar: {
    width: '150px',
    height: '150px',
    border: 'none',
  },
};

const MarketAvatar = withStyles(marketAvatarStyle)(({ classes, market }) => {
  let source = null;
  if (market.avatar_medium.indexOf('missing.png') !== -1) {
    return null;
  }
  source = ApiDirector.imageUrl(market.avatar_medium);

  return (
    <Avatar alt="Avatar" src={source} className={classes.avatar} />
  );
});

export const iconMap = {
  Produce: pear,
  'Meat, Fish, Poultry & Eggs': eggs,
  Dairy: cheese,
  'Baked Goods': cupcake,
  'Prepared Foods': jam,
  'Other Products': priceTag,
  Gifts: gift,
  'Holiday Pre-Orders': preOrder,
};

const zipsFor = (market) => {
  const zips = {};
  market.delivery_services.forEach((deliveryService) => {
    deliveryService.delivery_service_areas.forEach((zip) => {
      zips[zip] = true;
    });
  });
  return Object.keys(zips).sort().join(', ');
};

const MarketDetail = ({
  match: { params: { marketId } },
  expandAppBar,
}) => {
  const [market, setMarket] = useState();
  const [show, setShow] = useState(false);

  const marketCache = useCache({
    category: 'markets',
    key: marketId,
  });
  const [marketEntry] = marketCache;

  useEffect(() => {
    resolveCache(
      marketCache,
      () => fetchMarket(marketId),
    ).then((m) => setMarket(m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketId, marketEntry]);

  useEffect(() => {
    expandAppBar(
      <BreadCrumb primaryText="Favorite and Featured Markets" to="/customer-home" />,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!market) {
    return (<Loading>finding out who&apos;s coming to market</Loading>);
  }
  return (
    <Page>
      {Console.log('id: ', market.id)}
      <Inset>
        <div css={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
          <MarketAvatar market={market} />
        </div>
        <div css={{ marginTop: '0.8rem' }} />
        <Name>
          { market.name }
        </Name>
        <div css={{ color: MediumGray, fontSize: '0.9em' }}>
          { market.street_address_1 }
        </div>
        { market.street_address_2 !== ''
          && (
          <div css={{ color: MediumGray, fontSize: '0.9em' }}>
            { market.street_address_2 }
          </div>
          )}
        <div css={{ color: MediumGray, fontSize: '0.9em' }}>
          { market.city }, { market.state }
        </div>
        { market.supports_ordering
          && (
          <div className="flex flex-col">
            <div css={{ color: MediumGray, fontSize: '0.8em', marginTop: '0.4em' }}>
              Online Ordering Available
            </div>
            <div className="flex flex-col" css={{ color: MediumGreen, fontSize: '0.8em', marginTop: '0.4em' }}>
              {market.supports_delivery && (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faTruck} className="w-4 h-4" /> <p>Delivery</p>
                </div>
              )}
              {market.supports_community_hub && (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faBuildingWheat} className="w-4 h-4" /> <p>Community Hub</p>
                </div>
              )}
              {market.supports_pickup && (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faBagShopping} className="w-4 h-4" /> <p>Pick Up</p>
                </div>
              )}
            </div>
          </div>
          )}
        { market.supports_delivery
          && (
            <div>
              <ZipModal
                buttonText="Delivery Available to:"
                show={show}
                setShow={setShow}
                zips={(
                  <span css={{ color: MediumGray, fontSize: '0.7em' }}>
                    {zipsFor(market)}
                  </span>
                )}
              />
            </div>
          )}
        { market.description !== ''
          && (
          <div style={{ marginTop: '5px', marginBottom: '5px' }} css={{ color: MediumGray, fontSize: '0.8em' }}>
            { market.description }
          </div>
          )}
      </Inset>
      <div css={{ height: '1px', borderBottom: `1px solid ${LightGray}`, marginBottom: '1.0rem' }} />
      <div css={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: '0.5em', marginRight: '0.5em', marginBottom: '0.5em',
      }}
      >
        <div css={{ color: DarkGreen, fontFamily: 'Aleo', fontSize: '1.4rem' }}>
          Upcoming Market Days
          <div css={{
            height: '1px', borderBottom: `1px solid ${MediumGray}`, marginTop: '0.5rem', marginLeft: '-2rem', marginRight: '-2rem',
          }}
          />
        </div>
        {
          (market.market_events.length > 0)
          && market.market_events.slice(0, 3).map((marketEvent) => (
            <>
              {Console.log('ME: ', marketEvent.id)}
              <MarketEventDetail
                key={marketEvent.id}
                market_event={marketEvent}
              />
            </>
          ))
        }
      </div>
      {
        market
        && market.product_categories
        && market.product_categories.map((category) => (
          <Accordion
            key={category.category_name}
            defaultExpanded={market.product_categories.length === 1}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon className="text-white" />}
              aria-controls={`panel${category.id}-content`}
              id={`panel${category.id}-header`}
              className="p-0 pr-4 pl-4 text-lg text-white bg-green-medium font-serif"
            >
              <div
                className="h-7 w-7 overflow-hidden -mt-0.5 mr-2 br-1 rounded-2xl bg-white"
              >
                <img alt="" src={iconMap[category.category_name]} css={{ height: '30px', width: '30px' }} />
              </div>
              { category.category_name }
            </AccordionSummary>
            <AccordionDetails>
              <div className="mt-1.5">
                {
                  category.vendors.map((vendor) => (
                    <Card key={vendor.id} style={{ padding: '0.5rem' }}>
                      <LinkButton
                        style={{ fontFamily: 'Aleo' }}
                        to={`${market.id}/vendor/${vendor.id}#${toSnakeCase(category.category_name)}`}
                      >
                        <div css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          <div css={{ flex: '0 0 70px' }}>
                            { vendor.market2day_services
                              && market.supports_ordering
                              && (
                              <Avatar
                                css={{
                                  backgroundColor: '#ffffff',
                                  color: '#fffced',
                                  marginLeft: '1rem',
                                  border: 'none',
                                }}
                                src={ApiDirector.imageUrl(vendor.avatar)}
                              />
                              )}
                          </div>
                          <div css={{ flex: '1 1 auto' }}>
                            {vendor.name}
                          </div>
                        </div>
                      </LinkButton>
                    </Card>
                  ))
                }
              </div>
            </AccordionDetails>
          </Accordion>
        ))
      }
    </Page>
  );
};

export default withAuthentication(MarketDetail, false);
